import {
	IconAlertCircle,
	IconCalendar,
	IconCalendarExclamation,
	IconChevronDown,
	IconChevronLeft,
	IconChevronRight,
	IconClock,
	IconClockFilled,
	IconGavel,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Button } from "../core/buttons/button";
import "react-datepicker/dist/react-datepicker.css";
import CaseCalendarPrintModal from "@/components/cases/case-calendar-print-modal";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket.tsx";
import { useModal } from "@/components/core/modals/use-modal";
import { entityType } from "@/constants/entity-types";
import { calendarsQueryOptions } from "@/features/dashboard/api/queries/calendar-queries";
import { eventsQueryOptions } from "@/features/dashboard/api/queries/event-queries";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { TabsContent, TabsList } from "@radix-ui/react-tabs";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { addDays, format, parseISO, subDays } from "date-fns";
import Alert from "../core/alert";
import Badge from "../core/badge";
import { ScrollArea } from "../core/scroller";
import { Tabs, TabsTrigger } from "../core/tabs";

export default function CalendarDailyDetails({ handleDateClick, date }: { calendarView: boolean; handleDateClick: () => void; date?: string }) {
	const [selectedDate, setSelectedDate] = useState(date || new Date());
	const formattedSelectedDate = format(selectedDate, "yyyy-MM-dd");
	const { user } = useAuth();
	const modal = useModal();
	const isOcc = user?.is_occ;
	const calendarsQueries = useSuspenseQuery(
		calendarsQueryOptions({
			entity_type: entityType.USER,
		}),
	);
	const calendars = calendarsQueries.data;

	const eventsQuery = useSuspenseQuery(
		eventsQueryOptions({
			calendars: calendars.data.length !== 0 ? calendars.data.map((cal) => cal.id) : search.calendars,
			date: formattedSelectedDate,
			include: "caseRecord",
		}),
	);
	const events = eventsQuery.data.data;

	const allEventsQuery = useSuspenseQuery(
		eventsQueryOptions({
			calendars: calendars.data.length !== 0 ? calendars.data.map((cal) => cal.id) : search.calendars,
			include: "caseRecord",
		}),
	);
	const allEvents = allEventsQuery.data.data;

	const filteredEvents = events.filter((event) => {
		const startAt = format(event.start, "yyyy-MM-dd");
		// const isNotHoliday = event.type !== "holiday";
		const isNotHoliday = event.type !== "holiday" && event.type !== "events";
		return startAt === formattedSelectedDate && isNotHoliday;
	});

	const upcomingEvents = allEvents.filter((event) => {
		const startAt = format(event.start, "yyyy-MM-dd");
		// const isNotHoliday = event.type !== "holiday";
		const isNotHoliday = event.type !== "holiday" && event.type !== "events";
		return startAt > formattedSelectedDate && isNotHoliday;
	});

	const filteredHoliday = events.filter((event) => {
		const startAt = format(event.start, "yyyy-MM-dd");
		const isHoliday = event.type === "holiday";
		return startAt === formattedSelectedDate && isHoliday;
	});

	useEffect(() => {
		if (date) {
			setSelectedDate(parseISO(date));
		}
	}, [date]);

	const handlePrevDate = () => {
		setSelectedDate((prevDate) => subDays(prevDate, 1));
	};

	const handleNextDate = () => {
		setSelectedDate((prevDate) => addDays(prevDate, 1));
	};

	const [viewMode, setViewMode] = useState<"table" | "list">("table");

	const toggleView = () => {
		setViewMode((prev) => (prev === "table" ? "list" : "table"));
	};

	const openPrintModal = () => {
		const branchId = user?.branches?.[0]?.assignable?.id;
		const calendarIds = calendars?.data?.map((calendar) => calendar.id);
		const modalId = modal.openModal({
			allowClose: true,
			title: "",
			size: "sm",
			showHeader: true,
			showFooter: false,
			component: (
				<CaseCalendarPrintModal
					date={formattedSelectedDate}
					branchId={branchId}
					calendarIds={calendarIds}
					// data={activities}
					// stage={currentTab}
					handleSubmit={(values) => {
						console.log(values, "testing");
					}}
				/>
			),
		});
	};

	return (
		<div className="flex flex-col w-full">
			{/* Header Section */}
			<div className="flex flex-row items-center justify-between w-full h-[60px] px-6 gap-4 border-b border-border/50">
				<div className="inline-flex flex-row w-auto">
					<Button type="button" size="sm" onClick={handleDateClick}>
						<IconCalendar size={16} stroke={2} />
						<span className="">Calendar View</span>
					</Button>
				</div>
				<div className="inline-flex flex-row w-auto">
					<DatePicker
						selected={selectedDate}
						onChange={(date: Date) => setSelectedDate(date)}
						dateFormat="MMM d"
						customInput={
							<button type="button" className="group inline-flex flex-row items-center gap-1">
								<span className="text-lg font-semibold text-foreground-dark">{format(selectedDate, "MMMM d")}</span>
								<IconChevronDown className="text-foreground/50 group-hover:text-foreground-dark" size={18} stroke={2} />
							</button>
						}
						renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
							<div className="flex items-center justify-between px-4 py-2 bg-gray-100 rounded-t-lg">
								<button type="button" onClick={decreaseMonth} className="p-1 text-gray-700 hover:text-black">
									<IconChevronLeft size={16} />
								</button>
								<span className="text-sm font-semibold">{format(date, "MMMM yyyy")}</span>
								<button type="button" onClick={increaseMonth} className="p-1 text-gray-700 hover:text-black">
									<IconChevronRight size={16} />
								</button>
							</div>
						)}
					/>
				</div>
				<div className="inline-flex flex-row w-auto gap-1">
					<Button onClick={handlePrevDate} type="button" size="sm">
						<IconChevronLeft size={18} stroke={2} />
					</Button>
					<Button onClick={handleNextDate} type="button" size="sm">
						<IconChevronRight size={18} stroke={2} />
					</Button>
				</div>
			</div>

			{/* Events Section */}
			<div className="flex flex-col w-full">
				<Tabs className="" defaultValue="events" variant="compact">
					<TabsList className="px-6 pt-4 border-b border-border/50">
						<TabsTrigger value="events">Events ({filteredEvents.length})</TabsTrigger>
						<TabsTrigger value="holidays">Holidays ({filteredHoliday.length})</TabsTrigger>
					</TabsList>
					<TabsContent value="events">
						<div className="flex flex-col w-full p-6 gap-4">
							<div className="flex flex-row items-center justify-between w-full gap-4">
								<span className="text-base font-semibold">List of Events</span>
								{/* <Button type="button" size="sm" onClick={toggleView}>
									{viewMode === "table" ? "Switch to List View" : "Switch to Table View"}
								</Button> */}
								<div className="flex flex-row justify-end gap-2">
									<Button type="button" size="sm" onClick={toggleView}>
										{viewMode === "table" ? "Switch to List View" : "Switch to Table View"}
									</Button>
									<Button type="button" size="sm" onClick={openPrintModal}>
										Print
									</Button>
								</div>
							</div>

							{viewMode === "list" && (
								<div className="flex flex-col w-full gap-4">
									{filteredEvents.map((event) => {
										const counsels = Array.from(
											new Map((event.caseRecord?.case_counsels || []).filter((counsel) => counsel?.user).map((counsel) => [counsel.user.id, counsel])).values(),
										);
										return (
											<div key={event.id} className="flex flex-col w-full rounded-md border border-slate-200 overflow-hidden">
												<div className="flex flex-row items-center w-full gap-2 py-2 px-3 border-b border-slate-200 bg-slate-50">
													<div className="inline-flex justify-center items-center flex-col h-full w-auto rounded overflow-hidden">
														<div className="inline-flex flex-row items-center w-auto gap-1 py-1 px-2 pr-3 bg-green-600 text-white">
															<IconClock size={14} stroke={2} />
															<span className="text-sm font-bold">{format(new Date(event.start), "hh:mm a")}</span>
														</div>
														<div className="flex flex-row justify-center w-full py-1 px-2 bg-green-100 text-green-700 text-sm font-semibold ">
															{event.caseActivity?.hearing_mode}
														</div>
														{/*<Badge className="uppercase" size="xs" color="green">
															<IconClock size={14} stroke={2} />
															<span className="text-sm">
																{format(new Date(event.start), "hh:mm a")}
																{event.caseActivity?.hearing_mode && ` - ${event.caseActivity.hearing_mode}`}
															</span>
														</Badge>*/}
													</div>
													<div className="flex flex-col w-auto gap-1">
														<Badge className="self-start uppercase" size="xs" color="default-light">
															<IconGavel size={14} stroke={2} />
															<span className="">{event.caseRecord?.stage}</span>
														</Badge>
														{isOcc ? (
															<Link
																className="text-sm font-semibold text-blue-600 hover:text-blue-500"
																to="/case-classification/$caseClassificationId"
																params={{ caseClassificationId: event?.case_record_id || "" }}
															>
																<span>
																	{event?.caseRecord?.case_number || "n/a"} — {event.caseRecord?.case_title || "n/a"} — {event.title}
																</span>
															</Link>
														) : (
															<Link
																className="text-sm font-semibold text-blue-600 hover:text-blue-500"
																to="/case-evaluation/$caseEvaluationId"
																params={{ caseEvaluationId: event?.case_record_id || "" }}
															>
																<span>
																	{event?.caseRecord?.case_number || "n/a"} — {event.caseRecord?.case_title || "n/a"} — {event.title}
																</span>
															</Link>
														)}
													</div>
													{/* <Badge className="ml-auto uppercase" size="xs"> */}
													{/* <IconGavel size={12} /> */}
													{/*<span className="ml-auto uppercase text-sm text-foreground/70">{event.caseRecord?.stage}</span>*/}
													{/* </Badge> */}
												</div>
												<div className="flex flex-col w-full px-3 divide-y divide-dashed divide-slate-300 ">
													<div className="grid grid-cols-2 w-full py-2">
														<div className="col-span-1 flex flex-col gap-1">
															<span className="text-sm text-foreground/70">Plaintiff Counsel/s</span>
															<div className="flex flex-row flex-wrap w-full gap-1.5">
																{counsels.length > 0 ? (
																	counsels
																		.filter((counsel) => counsel.role === "Counsel (P)")
																		.map((counsel) => (
																			<div
																				key={counsel.user.id}
																				className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-slate-100 text-sm border-l-2 border-slate-400 text-foreground-dark"
																			>
																				ATTY. {counsel.user.first_name} {counsel.user.last_name}
																			</div>
																		))
																) : (
																	<span className="text-sm font-semibold text-foreground-dark">No Counsel Listed</span>
																)}
															</div>
														</div>
														<div className="col-span-1 flex flex-col gap-1">
															<span className="text-sm text-foreground/70">Defendant Counsel/s</span>
															<div className="flex flex-row flex-wrap w-full gap-1.5">
																{counsels.length > 0 ? (
																	counsels
																		.filter((counsel) => counsel.role === "Counsel (D)")
																		.map((counsel) => (
																			<span
																				key={counsel.user.id}
																				className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-slate-100 text-sm border-l-2 border-slate-400 text-foreground-dark"
																			>
																				ATTY. {counsel.user.first_name} {counsel.user.last_name}
																			</span>
																		))
																) : (
																	<span className="text-sm font-semibold text-foreground-dark">No Counsel Listed</span>
																)}
															</div>
														</div>
													</div>
													<div className="grid grid-cols-2 w-full py-2">
														{event.caseActivity?.witness_name?.filter((w) => w.parties === "Plaintiff").length !== 0 ? (
															<div className="col-span-1 flex flex-col gap-1">
																<span className="text-sm text-foreground/70">Plaintiff Wtiness/es</span>
																<div className="flex flex-row flex-wrap w-full gap-1.5">
																	{event?.caseActivity?.witness_name?.length !== 0 ? (
																		event.caseActivity?.witness_name
																			?.filter((w) => w.parties === "Plaintiff")
																			.map((witness) => (
																				<div key={witness?.value} className="">
																					<span className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-slate-100 text-sm border-l-2 border-slate-400 text-foreground-dark">
																						{witness.label}
																					</span>
																				</div>
																			))
																	) : (
																		<div className="text-sm font-semibold text-foreground-dark">No Data</div>
																	)}
																</div>
															</div>
														) : (
															<div className="col-span-1 flex flex-col gap-1">
																<span className="text-sm text-foreground/70">Defendant Wtiness/es</span>
																<div className="flex flex-row flex-wrap w-full gap-1.5">
																	{event?.caseActivity?.witness_name?.length !== 0 ? (
																		event.caseActivity?.witness_name
																			?.filter((w) => w.parties === "Defendant")
																			?.map((witness) => (
																				<div key={witness?.value} className="">
																					<span className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-slate-100 text-sm border-l-2 border-slate-400 text-foreground-dark">
																						{witness.label}
																					</span>
																				</div>
																			))
																	) : (
																		<div className="text-sm font-semibold text-foreground-dark">No Data</div>
																	)}
																</div>
															</div>
														)}
													</div>

													<div className="flex flex-row w-full py-2">
														<div className="flex flex-col gap-1">
															<span className="text-sm text-foreground/70">Exhibit/s</span>
															<div className="flex flex-row flex-wrap w-full gap-1.5">
																{event?.caseActivity?.exhibits?.length !== 0 ? (
																	event.caseActivity?.exhibits?.map((exhibit) => (
																		<div key={exhibit?.value} className="">
																			<button
																				onClick={() => {
																					const url = `/case-evaluation/${event?.caseRecord?.id}?tabs=case-evidence&exhibit=${exhibit?.value}&activity=${event?.caseActivity?.id}`;
																					window.open(url, "_blank");
																				}}
																				type="button"
																				className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-blue-50 text-sm border-l-2 border-blue-400 text-blue-600 cursor-pointer"
																			>
																				{exhibit.label}
																			</button>
																		</div>
																	))
																) : (
																	<div className="text-sm font-semibold text-foreground-dark">No Data</div>
																)}
															</div>
														</div>
													</div>
													<div className="flex flex-row w-full py-2">
														<div className="flex flex-col gap-1">
															<span className="text-sm text-foreground/70">Upcoming Schedule</span>
															<div className="flex flex-row flex-wrap w-full gap-1.5">
																{upcomingEvents
																	.filter((date) => date.caseRecord?.id === event?.caseRecord?.id)
																	.map((upcoming) => (
																		<div
																			key={upcoming.id}
																			className="inline-flex flex-row items-center w-auto h-[20px] px-1.5 bg-slate-100 text-sm border-l-2 border-slate-400 text-foreground-dark"
																		>
																			{`${formatDate(upcoming.start)}`}
																			{event?.caseActivity?.hearing_mode && ` - ${event.caseActivity?.hearing_mode}`}
																		</div>
																	))}
															</div>
														</div>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							)}

							{viewMode === "table" && (
								<div className="flex flex-col w-full">
									{filteredEvents.length > 0 ? (
										<div className="flex flex-col w-full rounded-md border border-border/50">
											<table className="w-full text-sm">
												<thead className="border-b-2 border-border/50">
													<tr>
														<th className="text-left text-foreground-dark py-3 px-4">Case No.</th>
														<th className="text-left text-foreground-dark py-3 px-4">Case Title</th>
														<th className="text-left text-foreground-dark py-3 px-4">Counsel/s</th>
														<th className="text-left text-foreground-dark py-3 px-4">Stage</th>
														<th className="text-left text-foreground-dark py-3 px-4">Exhibits</th>
														<th className="text-left text-foreground-dark py-3 px-4">Witness</th>
														<th className="text-left text-foreground-dark py-3 px-4">Activity</th>
														<th className="text-right text-foreground-dark py-3 px-4">Schedule</th>
														<th className="text-right text-foreground-dark py-3 px-4">Upcoming Schedule/s</th>
													</tr>
												</thead>
												<tbody>
													{filteredEvents.map((event) => {
														const counsels = Array.from(
															new Map(
																(event.caseRecord?.case_counsels || []).filter((counsel) => counsel?.user).map((counsel) => [counsel.user.id, counsel]),
															).values(),
														);
														return (
															<tr key={event.id} className="border-b border-border/50 last:border-b-0">
																<td className="text-left text-foreground py-3 px-4">
																	{isOcc ? (
																		<Link
																			className="font-semibold text-blue-600 hover:text-blue-500"
																			to="/case-classification/$caseClassificationId"
																			params={{ caseClassificationId: event?.case_record_id || "" }}
																		>
																			{event?.caseRecord?.case_number || "n/a"}
																		</Link>
																	) : (
																		<Link
																			className="font-semibold text-blue-600 hover:text-blue-500"
																			to="/case-evaluation/$caseEvaluationId"
																			params={{ caseEvaluationId: event?.case_record_id || "" }}
																		>
																			{event?.caseRecord?.case_number || "n/a"}
																		</Link>
																	)}
																</td>
																<td className="text-left text-foreground py-3 px-4">{event.caseRecord?.case_title || "n/a"}</td>
																<td className="text-left text-foreground py-3 px-4">
																	{event.caseActivity?.hearing_mode ? (
																		<div className="flex flex-col w-full">
																			{counsels.length > 0 ? (
																				<div className="flex flex-col w-full p-4 gap-4">
																					<div className="flex flex-col w-full gap-2">
																						<div className="flex flex-row items-center w-full gap-2">
																							<Badge className="uppercase" size="xs" color="blue">
																								Plaintiff
																							</Badge>
																							<div className="flex flex-row w-full border-t border-border/50" />
																						</div>
																						{/* <div className="flex flex-row flex-wrap gap-1">
																							{counsels
																								.filter((counsel) => counsel.role === "Counsel (P)")
																								.map((counsel, index, arr) => (
																									<span key={counsel.user.id} className="text-sm text-foreground-dark font-semibold">
																										ATTY. {counsel.user.first_name} {counsel.user.last_name}
																										{index < arr.length - 1 && ", "}
																										{console.log(counsel)}
																									</span>
																								))}
																						</div> */}
																						<div className="flex flex-row flex-wrap gap-1">
																							{event.plaintiffCounsels &&
																							event.plaintiffCounsels.length > 0 &&
																							event.plaintiffCounsels.some((counsel) => counsel.first_name && counsel.last_name)
																								? event.plaintiffCounsels
																										.filter((counsel) => counsel.first_name && counsel.last_name)
																										.map((counsel, index, arr) => (
																											<span key={counsel.id || index} className="text-sm text-foreground-dark font-semibold">
																												ATTY. {counsel.first_name} {counsel.last_name}
																												{index < arr.length - 1 && ", "}
																											</span>
																										))
																								: (counsels || [])
																										.filter((counsel) => counsel.role === "Counsel (P)")
																										.map((counsel, index, arr) => (
																											<span key={counsel.user.id || index} className="text-sm text-foreground-dark font-semibold">
																												ATTY. {counsel.user.first_name} {counsel.user.last_name}
																												{index < arr.length - 1 && ", "}
																											</span>
																										))}
																						</div>
																					</div>
																					<div className="flex flex-col w-full gap-2">
																						<div className="flex flex-row items-center w-full gap-2">
																							<Badge className="uppercase" size="xs" color="red">
																								Defendant
																							</Badge>
																							<div className="flex flex-row w-full border-t border-border/50" />
																						</div>
																						<div className="flex flex-row flex-wrap gap-1">
																							{counsels
																								.filter((counsel) => counsel.role === "Counsel (D)")
																								.map((counsel, index, arr) => (
																									<span key={counsel.user.id} className="text-sm text-foreground-dark font-semibold">
																										ATTY. {counsel.user.first_name} {counsel.user.last_name}
																										{index < arr.length - 1 && ", "}
																									</span>
																								))}
																						</div>
																					</div>
																				</div>
																			) : (
																				<div className="flex flex-col w-full p-4">
																					<Alert type="default" allowClose={false}>
																						<div className="flex justify-between items-center w-full">
																							<div className="flex items-center gap-2">
																								<IconAlertCircle size={20} stroke={2} />
																								<span className="font-semibold">No counsel listed</span>
																							</div>
																						</div>
																					</Alert>
																				</div>
																			)}
																		</div>
																	) : (
																		"N/A"
																	)}
																</td>
																<td className="text-left text-foreground py-3 px-4">{event.caseRecord?.stage}</td>
																<td className="text-left text-foreground py-3 px-4">
																	{event?.caseActivity?.exhibits?.map((exhibit) => (
																		<div key={exhibit?.value} className="flex flex-col w-full gap-2">
																			<button
																				onClick={() => {
																					const url = `/case-evaluation/${event?.caseRecord?.id}?tabs=case-evidence&exhibit=${exhibit?.value}&activity=${event?.caseActivity?.id}`;
																					window.open(url, "_blank");
																				}}
																				type="button"
																				className="text-foreground"
																			>
																				{exhibit.label}
																			</button>
																		</div>
																	))}
																</td>
																<td className="text-left text-foreground py-3 px-4">
																	{event?.caseActivity?.witness_name?.map((witness) => (
																		<div key={witness?.value} className="flex flex-col w-full gap-2">
																			<button
																				onClick={() => {
																					const url = `/case-evaluation/${event?.caseRecord?.id}?tabs=case-evidence&exhibit=${witness?.value}&activity=${event?.caseActivity?.id}`;
																					window.open(url, "_blank");
																				}}
																				type="button"
																				className="text-foreground"
																			>
																				{witness.label}
																			</button>
																		</div>
																	))}
																</td>
																<td className="text-left text-foreground py-3 px-4">{event.title}</td>
																<td className="text-right text-foreground py-3 px-4">
																	{formatDate(event.start)}
																	{event?.caseActivity?.hearing_mode && ` - ${event.caseActivity?.hearing_mode}`}
																</td>
																<td className="text-right text-foreground py-3 px-4">
																	{upcomingEvents
																		.filter((date) => date.caseRecord?.id === event?.caseRecord?.id)
																		.map((upcoming) => (
																			<div key={upcoming.id} className="flex flex-col w-full gap-2">
																				<span className="text-foreground/70">
																					{formatDate(upcoming.start)}
																					{upcoming?.caseActivity?.hearing_mode && ` - ${upcoming.caseActivity?.hearing_mode}`}
																				</span>
																			</div>
																		))}
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									) : (
										<Blanket className="" width="sm">
											<BlanketImage icon={<IconCalendarExclamation size={30} stroke={1.5} />} />
											<BlanketInfo title="No Cases Registered" message="No available cases for this day, please contact administrator for further questions." />
										</Blanket>
									)}
								</div>
							)}
						</div>
					</TabsContent>

					{/* Holiday Section */}
					<TabsContent value="holidays">
						<div className="flex flex-col w-full p-6">
							{filteredHoliday.length > 0 ? (
								<div className="flex flex-col w-full rounded-md border border-border/50">
									<table className="w-full text-sm">
										<thead className="border-b-2 border-border/50">
											<tr>
												<th className="text-left text-foreground-dark py-3 px-4">Event Title</th>
												<th className="text-left text-foreground-dark py-3 px-4">Activity</th>
												<th className="text-right text-foreground-dark py-3 px-4">Date</th>
											</tr>
										</thead>
										<tbody>
											{filteredHoliday.map((event) => (
												<tr key={event.id} className="border-b border-border/50 last:border-b-0">
													<td className="text-left text-foreground py-3 px-4">{event.title}</td>
													<td className="text-left text-foreground py-3 px-4">{event.type}</td>
													<td className="text-right text-foreground py-3 px-4">{formatDate(event.start)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<Blanket className="" width="sm">
									<BlanketImage icon={<IconCalendarExclamation size={30} stroke={1.5} />} />
									<BlanketInfo title="No Holiday" message="No Holiday for this day, please contact administrator for further questions." />
								</Blanket>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
